/**
 * PublishReadinessModal (web) — twin of mobile's
 * src/app/components/PublishReadinessModal.tsx. The "finish setting up
 * your profile" dialog that lists exactly which publish-readiness rules
 * a user still has to satisfy before going public, each with a checkbox
 * so they see what's done and what's left.
 *
 * Presentational only — the live rule list + open/close wiring live in
 * PublishReadinessHost. Mirrors PaywallSheet's fixed-scrim + ESC +
 * scroll-lock pattern.
 */
import { useEffect } from 'react';

export type PublishRule = {
  id: string;
  label: string;
  passed: boolean;
};

export function PublishReadinessModal({
  open,
  rules,
  onOpenBuilder,
  onClose,
}: {
  open: boolean;
  rules: PublishRule[];
  onOpenBuilder: () => void;
  onClose: () => void;
}) {
  // ESC closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Body scroll-lock while open.
  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Finish setting up your profile"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(43,29,20,0.5)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        animation: 'fade-in 0.25s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 440,
          background: 'var(--surface)',
          borderRadius: 22,
          padding: '28px 28px 22px',
          maxHeight: '90vh',
          overflowY: 'auto',
          animation: 'fade-in 0.25s ease',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontSize: 24,
            fontWeight: 600,
            color: 'var(--ink)',
          }}
        >
          Finish setting up your profile
        </h2>
        <p
          style={{
            margin: '8px 0 18px',
            fontFamily: 'var(--font-ui)',
            fontSize: 15,
            color: 'var(--ink-2)',
          }}
        >
          Before your profile can be visible, you need to:
        </p>

        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {rules.map((r) => (
            <li
              key={r.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '8px 0',
              }}
            >
              <CheckBox checked={r.passed} />
              <span
                style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 15,
                  fontWeight: 500,
                  color: r.passed ? 'var(--ink-3)' : 'var(--ink)',
                  textDecoration: r.passed ? 'line-through' : 'none',
                }}
              >
                {r.label}
              </span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={onOpenBuilder}
          style={{
            marginTop: 22,
            width: '100%',
            padding: '13px 16px',
            borderRadius: 14,
            border: 'none',
            background: 'var(--coral)',
            color: '#fff',
            fontFamily: 'var(--font-ui)',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Open builder
        </button>
        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: 8,
            width: '100%',
            padding: '11px 16px',
            borderRadius: 14,
            border: 'none',
            background: 'transparent',
            color: 'var(--ink-3)',
            fontFamily: 'var(--font-ui)',
            fontSize: 15,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function CheckBox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        flex: '0 0 auto',
        width: 22,
        height: 22,
        borderRadius: 6,
        border: `2px solid ${checked ? 'var(--coral)' : 'var(--line-strong)'}`,
        background: checked ? 'var(--coral)' : 'transparent',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {checked ? (
        <svg width={12} height={12} viewBox="0 0 24 24" fill="none">
          <path
            d="M5 13l4 4L19 7"
            stroke="#fff"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
    </span>
  );
}
